// @ts-check
// =============================================================================
// js/screens/deck-builder.js — create, edit, and manage decks.
//
// WHAT THIS IS (plan/deck-builder.md; old RenderDeck(Text) :2531/:2726)
//   Route: #/deck/<index>
//     <slot>            edit the saved deck in that slot (0–99)
//     new-<hero>-<slot> start a fresh deck for a hero in a free slot
//     100–111           VIEW a prebuilt (read-only — prebuilts can't change)
//
// PORTED RULES (gameplay-critical, via the shared rules bundle)
//   - VerifyDeck gives the exact save-blocking reason ("Can't Save: …");
//     the server re-validates, so the client check is UX only.
//   - Per-card copy limits (cards[id].limit) enforced on add.
//   - A deck trimmed to ≤15 cards is DELETED on save ("EATING YOUR DECK") —
//     kept, plus an explicit confirm so it's no longer a surprise gesture.
//   - Hero picker: current hero shown; tap to expand all (old showHeros).
//   - Export: quantity-grouped decklist to the clipboard (old :2974).
// =============================================================================

import { store, notify } from '../core/store.js';
import { navigate } from '../core/router.js';
import { request } from '../net/http.js';
import { can, showLoginOptions, JOIN_URL } from '../app/session.js';
import { callStatsAPI } from '../app/analytics.js';
import { cardFilters, VerifyCard, VerifyDeck } from '../rules.js';
import { initCardRenderer } from '../render/card-tile.js';
import { renderCardCached, showImageZoom, hideImageZoom, cardLoadingHtml } from '../render/card-images.js';
import { selectDeck, HERO_ASSET_NAMES } from '../ui/deck-list.js';
import { openArtPicker } from '../ui/art-picker.js';
import { cardSortValue, compareCardsForDisplay } from '../ui/card-order.js';
import { resolveDisplayArt, rememberArt } from '../ui/art-prefs.js';
import { mountGridSizeControls, applyGridSize } from '../ui/grid-size.js';
import { showUpsell } from './../ui/upsell.js';
import { escapeHtml } from '../ui/modal.js';
import { toast } from '../ui/toast.js';

/** The deck being edited (a live reference into userData.decks). */
let deck = /** @type {any} */ (null);

/** Read-only mode (prebuilt viewer). */
let readOnly = false;

/** Expand the hero picker to all heroes? (old showHeros) */
let showAllHeros = false;

/** Card-pool filter. */
let cardFilter = 'None';

/** @type {HTMLElement|null} */
let screenEl = null;

/** Abandons stale async image fills after re-render. */
let buildStamp = 0;

/** Pool card shells keyed by card id, so add/remove can update ONE card's
 *  copy dots in place instead of repainting the whole grid. */
let shellByCardId = /** @type {Map<number, HTMLElement>} */ (new Map());

/**
 * Render the builder for #/deck/<index>.
 * @param {HTMLElement} container
 * @param {Record<string,string>} params
 * @returns {void}
 */
export function render(container, params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen screen--wide'; // card grids fill the desktop width
	container.appendChild(screenEl);

	const userData = store.userData;
	if (!userData) return;

	const indexParam = String(params.index || '');

	// Guests can't deckBUILD — show the builder LOCKED with the free-Patreon
	// pitch instead of bouncing them away. Prebuilt VIEWING (index >= 100,
	// read-only) stays open: that's how guests browse the decks they duel
	// the bot with.
	if (!can('deckbuild') && !(Number(indexParam) >= 100)) {
		renderLockedBuilder();
		showUpsell('saveDeck');
		return;
	}
	readOnly = false;
	if (indexParam.startsWith('new-')) {
		// new-<hero>-<slot>: seed a fresh deck (old NewDeck :2512).
		const [, heroId, slot] = indexParam.split('-');
		callStatsAPI('Deckbuilding:New');
		const slotIndex = parseInt(slot, 10);
		userData.decks[slotIndex] = {
			index: slotIndex,
			nickname: '',
			list: [],
			hero: Number(heroId),
			dateCreated: Date.now(),
			dateModified: Date.now(),
			meta_plays: 0,
			meta_wins: 0,
			meta_losses: 0,
			starred: false,
		};
		deck = userData.decks[slotIndex];
	} else if (Number(indexParam) >= 100) {
		// Prebuilt viewer.
		deck = userData.defaultdecks?.find((d) => d && d.index === Number(indexParam));
		readOnly = true;
	} else {
		deck = userData.decks?.[Number(indexParam)];
	}

	if (!deck) {
		screenEl.innerHTML = `<h1 class="screen-title">Deck Builder</h1>
			<div class="empty-state">That deck doesn't exist. Pick one from the lobby!</div>`;
		return;
	}
	undoStack = []; // fresh deck -> nothing to undo yet
	void paint();
}

/** Abandon async fills. */
export function destroy() {
	buildStamp++;
	screenEl = null;
	deck = null;
}

/**
 * The guest-facing LOCKED builder: a padlocked stand-in explaining that
 * deckbuilding unlocks with a free Patreon account (linking one makes you a
 * Member — decision #9: Members keep deckbuilding).
 */
function renderLockedBuilder() {
	if (!screenEl) return;
	screenEl.innerHTML = `
		<h1 class="screen-title">Deck Builder</h1>
		<div class="deck-builder-locked">
			<div class="deck-builder-locked-icon">🔒</div>
			<div class="deck-builder-locked-title">Deck Building is Locked</div>
			<p>Guests duel with the prebuilt decks. Link a <b>free Patreon account</b>
			to unlock the deck builder and brew your own!</p>
			<div class="deck-builder-locked-actions">
				<button class="defaultButton" id="locked-join">Join Free on Patreon</button>
				<button class="defaultButton" id="locked-login">Log In</button>
				<button class="defaultButton" id="locked-back">Back To Lobby</button>
			</div>
		</div>`;
	screenEl.querySelector('#locked-join')?.addEventListener('click', () => window.open(JOIN_URL, '_blank'));
	screenEl.querySelector('#locked-login')?.addEventListener('click', showLoginOptions);
	screenEl.querySelector('#locked-back')?.addEventListener('click', () => navigate('lobby'));
}

/** Repaint the whole builder. */
async function paint() {
	if (!screenEl || !deck) return;
	const stamp = ++buildStamp;
	const { cards, heros } = store;

	// --- Header: legality + save/delete + name + export ----------------------
	// The count and the save/delete/can't-save button live in stable slots
	// (#deck-count / #deck-save-slot) so adding or removing a card can refresh
	// JUST those in place — no full repaint, and the name field keeps focus.
	let header = `
		<div class="deck-toolbar">
			<button class="defaultButton" id="btn-back">⇦ Lobby</button>
			<b>${readOnly ? 'Viewing' : 'Modifying'} Deck</b>
			<span>${escapeHtml(String(heros[deck.hero]?.name || '').split(',')[0])}
				<span id="deck-count"></span></span>`;
	if (!readOnly) {
		header += `<span id="deck-save-slot"></span>`;
		header += `<input type="text" id="deckname" class="textInput" placeholder="Deckname"
			value="${escapeHtml(deck.nickname || '')}">`;
	}
	header += `<span id="btn-export" class="deck-export" title="Copy decklist">📋</span></div>`;

	// --- Hero picker (old :2754) ---------------------------------------------
	// Selecting a leader (showAllHeros) shows the WHOLE roster wrapped across
	// rows; otherwise just the deck's current leader. Same card scale either way.
	let heroRow = `<div class="deck-hero-row hide-scrollbar">`;
	HERO_ASSET_NAMES.forEach((name, index) => {
		if (!showAllHeros && deck.hero !== index) return;
		heroRow += `<img class="deck-hero${deck.hero === index ? ' is-active' : ''}"
			data-hero="${index}" src="/leaders/${name}.png" alt="${name}">`;
	});
	heroRow += `</div>`;

	// --- Filter + size controls: dropdown LEFT, size buttons CENTERED. Lives at
	//     the top of the pool column so it sits BELOW the current deck (which
	//     stacks above the pool on narrow screens). ------------------------------
	let filterBar = `<div class="deck-filterbar">
		<label class="deck-filter-label">Add cards:
			<span class="select-wrapper"><select id="filterDropdown">`;
	for (const filter of ['None', ...cardFilters.filter((f) => f !== 'None')]) {
		filterBar += `<option value="${escapeHtml(filter)}"${filter === cardFilter ? ' selected' : ''}>
			${escapeHtml(filter === 'None' ? 'Legal Cards' : filter)}</option>`;
	}
	filterBar += `</select></span></label>
		<span id="grid-size-controls"></span></div>`;

	screenEl.innerHTML = `
		${header}${heroRow}
		<div class="deck-container">
			<div class="deck-left">
				${readOnly ? '' : filterBar}
				<div id="deck-pool"></div>
			</div>
			<div class="deck-right" id="deck-current"></div>
		</div>`;

	wireHeader();
	updateHeaderState();
	paintCurrentDeck();
	if (!readOnly) await paintPool(stamp);
}

/**
 * Refresh JUST the card count and the save/delete/can't-save button — called on
 * every add/remove so the toolbar stays live without a full repaint (which
 * would blow away the name field's focus and reload every pool image).
 */
function updateHeaderState() {
	if (!screenEl || !deck) return;
	const { cards, heros } = store;
	const countEl = screenEl.querySelector('#deck-count');
	if (countEl) countEl.textContent = `(${deck.list.length}/${heros[deck.hero]?.decksize ?? '?'})`;
	if (readOnly) return;
	const slot = screenEl.querySelector('#deck-save-slot');
	if (!slot) return;
	const verify = VerifyDeck(deck, cards, heros);
	slot.innerHTML = deck.list.length <= 15
		? `<button class="defaultButton" id="btn-save">Delete Deck</button>`
		: verify === true
			? `<button class="defaultButton" id="btn-save">Save Deck</button>`
			: `<span class="deck-cant-save">Can't Save: ${escapeHtml(String(verify))}</span>`;
	slot.querySelector('#btn-save')?.addEventListener('click', () => void saveDeck());
}

/** Wire the toolbar + hero picker. */
function wireHeader() {
	if (!screenEl) return;
	screenEl.querySelector('#btn-back')?.addEventListener('click', () => navigate('lobby'));
	// #btn-save lives in #deck-save-slot and is (re)wired by updateHeaderState.
	screenEl.querySelector('#btn-export')?.addEventListener('click', copyDecklist);
	screenEl.querySelector('#filterDropdown')?.addEventListener('change', (e) => {
		cardFilter = /** @type {HTMLSelectElement} */ (e.target).value;
		void paint();
	});
	mountGridSizeControls(
		/** @type {HTMLElement|null} */ (screenEl.querySelector('#grid-size-controls')),
		() => screenEl?.querySelector('#deck-pool') || null,
	);
	for (const heroImg of screenEl.querySelectorAll('.deck-hero')) {
		heroImg.addEventListener('click', () => {
			if (readOnly) return;
			const heroId = Number(heroImg.getAttribute('data-hero'));
			// Tap the current hero -> toggle the full picker (old AddHero :3195).
			if (deck.hero === heroId) {
				showAllHeros = !showAllHeros;
			} else {
				deck.hero = heroId;
				showAllHeros = false;
			}
			void paint();
		});
	}
}

/** The deck's max size for its hero (falls back to 30). */
function deckSizeFor() {
	return Number(store.heros?.[deck?.hero]?.decksize) || 30;
}

/** Current-deck view: 'list' or 'visual' (persisted). */
let deckView = (() => {
	try { return localStorage.getItem('deckView') === 'visual' ? 'visual' : 'list'; } catch { return 'list'; }
})();

/** Removed-card history for Undo (LIFO). Each entry is the deck-list object
 *  that was removed; undo pushes it back and re-sorts. Cleared per deck load. */
let undoStack = /** @type {any[]} */ ([]);

/** Re-add the most recently removed card (top-left Undo button). */
function undoRemove() {
	if (!undoStack.length) return;
	if (deck.list.length >= deckSizeFor()) {
		toast(`Deck is full (${deckSizeFor()} cards).`);
		return;
	}
	const entry = undoStack.pop();
	deck.list.push(entry);
	deck.list.sort((a, b) => cardSortValue(store.cards[a.id]) - cardSortValue(store.cards[b.id]));
	deck.dateModified = Date.now();
	updateCardDots(entry.id);
	paintCurrentDeck();
	updateHeaderState();
}

/**
 * The right column: the deck as a LIST or a VISUAL grid. Both fill exactly
 * `decksize` slots — real cards first (tap to remove), then empty placeholders
 * so the remaining room is always visible — and never exceed the max.
 */
function paintCurrentDeck() {
	const panel = screenEl?.querySelector('#deck-current');
	if (!panel) return;
	const { cards } = store;
	const decksize = deckSizeFor();
	const slots = Math.max(decksize, deck.list.length); // never hide over-cap cards

	const toggle = readOnly ? '' : `
		<div class="deck-view-toggle">
			<button class="deck-view-btn js-view-list${deckView === 'list' ? ' is-active' : ''}">List</button>
			<button class="deck-view-btn js-view-visual${deckView === 'visual' ? ' is-active' : ''}">Visual</button>
		</div>`;

	let body;
	if (deckView === 'visual') {
		body = '<div class="deck-visual">';
		for (let i = 0; i < slots; i++) {
			const card = deck.list[i] && cards[deck.list[i].id];
			body += card
				? `<div class="deck-visual-slot is-filled${readOnly ? '' : ' is-removable'}" data-index="${i}" title="${readOnly ? '' : 'Tap to remove'}">${cardLoadingHtml(card)}</div>`
				: '<div class="deck-visual-slot is-empty"></div>';
		}
		body += '</div>';
	} else {
		body = '<ul class="deck-list-view">';
		for (let i = 0; i < slots; i++) {
			const card = deck.list[i] && cards[deck.list[i].id];
			body += card
				? `<li class="deck-row${readOnly ? '' : ' deck-row--removable'}" data-index="${i}" title="${readOnly ? '' : 'Tap to remove'}">
						<span class="deck-row-cost">${card.cropCost || card.meatCost || card.waitCost || 0}</span>
						<span class="deck-row-name">${escapeHtml(card.name)}</span>
					</li>`
				: '<li class="deck-row deck-row--empty"><span class="deck-slot-empty">Empty slot</span></li>';
		}
		body += '</ul>';
	}

	// Undo button, top-left of the deck panel — only after something's removed.
	const undoBtn = (!readOnly && undoStack.length)
		? `<button class="deck-undo js-undo" title="Re-add the last removed card">↩ Undo</button>`
		: '';
	panel.innerHTML = `${undoBtn}<div class="deck-current-title">Current Deck (${deck.list.length}/${decksize})</div>${toggle}${body}`;

	panel.querySelector('.js-undo')?.addEventListener('click', undoRemove);
	panel.querySelector('.js-view-list')?.addEventListener('click', () => setDeckView('list'));
	panel.querySelector('.js-view-visual')?.addEventListener('click', () => setDeckView('visual'));

	if (!readOnly) {
		for (const el of panel.querySelectorAll('.deck-row--removable, .deck-visual-slot.is-removable')) {
			el.addEventListener('click', () => {
				// Old RemoveCard :3271 — splice by position. INCREMENTAL: update the
				// removed card's dots + this panel + the toolbar; the pool grid is
				// untouched. The removed entry goes on the Undo stack.
				const idx = Number(el.getAttribute('data-index'));
				const removed = deck.list[idx];
				deck.list.splice(idx, 1);
				deck.dateModified = Date.now();
				if (removed) {
					undoStack.push(removed);
					updateCardDots(removed.id);
				}
				paintCurrentDeck();
				updateHeaderState();
			});
		}
	}

	// Visual view: fill the filled slots' thumbnails (cached renders).
	if (deckView === 'visual') {
		const stamp = buildStamp;
		for (const el of panel.querySelectorAll('.deck-visual-slot.is-filled')) {
			const entry = deck.list[Number(el.getAttribute('data-index'))];
			const base = entry && cards[entry.id];
			if (!base) continue;
			renderCardCached({ ...base, selectedArt: entry.selectedArt || base.selectedArt }).then((src) => {
				if (stamp !== buildStamp || !src) return;
				const img = el.querySelector('img');
				if (img) { img.src = src; img.classList.remove('card-loading'); }
			});
		}
	}
}

/** Switch the current-deck view and persist it. */
function setDeckView(view) {
	deckView = view;
	try { localStorage.setItem('deckView', view); } catch { /* ignore */ }
	paintCurrentDeck();
}

/** How many copies of a card the deck currently holds. */
function countCopies(cardId) {
	return deck.list.filter((entry) => entry.id === cardId).length;
}

/** The copy-progress dots for a pool card: `limit` dots, first `copies` lit. */
function dotsHtml(limit, copies) {
	let html = '';
	for (let i = 0; i < limit; i++) {
		html += `<span class="deck-dot${i < copies ? ' is-filled' : ''}"></span>`;
	}
	return html;
}

/**
 * Light/unlight one pool card's copy dots to match the current deck — the whole
 * point of the incremental model (no grid repaint on add/remove).
 * @param {number} cardId
 */
function updateCardDots(cardId) {
	const shell = shellByCardId.get(cardId);
	const dots = shell?.querySelector('.deck-dots');
	if (!dots) return;
	const copies = countCopies(cardId);
	[...dots.children].forEach((dot, i) => dot.classList.toggle('is-filled', i < copies));
}

/**
 * The card pool grid: legal cards (per filter), tap to add.
 * @param {number} stamp
 */
async function paintPool(stamp) {
	const pool = screenEl?.querySelector('#deck-pool');
	if (!pool) return;
	await initCardRenderer();
	if (stamp !== buildStamp) return;
	const { cards, heros } = store;

	// Same cost/type/event order as the collection (the ported server index).
	// VerifyCard filters by hero + the dropdown ONLY — never by how many copies
	// are already in the deck — so the pool composition is STABLE across add/
	// remove, which is what lets those updates stay incremental (dots only).
	const sorted = [...cards].sort(compareCardsForDisplay);
	const list = sorted.filter(
		(card) => card.id !== 0 && VerifyCard(card, deck, cards, heros, cardFilter) === true,
	);

	pool.innerHTML = '';
	applyGridSize(pool);
	shellByCardId = new Map();
	/** @type {Array<{card:any, shell:HTMLElement}>} */
	const shells = [];
	for (const card of list) {
		resolveDisplayArt(card); // random-first, then remembered (never revert to #1)
		const shell = document.createElement('div');
		shell.className = 'gallery-shell deck-pool-card';
		shell.innerHTML = cardLoadingHtml(card);
		pool.appendChild(shell);
		shellByCardId.set(card.id, shell);
		shells.push({ card, shell });
	}

	for (let i = 0; i < shells.length; i += 4) {
		if (stamp !== buildStamp) return;
		await Promise.all(shells.slice(i, i + 4).map(({ card, shell }) => fillPoolShell(card, shell, stamp)));
	}
}

/**
 * One pool card: copy dots on top, the image (TAP to add, LONG-PRESS to zoom),
 * and the ⬅️ n/total ➡️ art switcher (same control as the cardpool). No
 * magnifier button — long-press is the zoom.
 * @param {any} card
 * @param {HTMLElement} shell
 * @param {number} stamp
 */
async function fillPoolShell(card, shell, stamp) {
	const src = await renderCardCached(card);
	if (stamp !== buildStamp || !src) return;
	const arts = card.collectedArts || [];
	const artIndex = Math.max(0, arts.indexOf(card.selectedArt));
	const limit = Math.max(1, Number(card.limit) || 1);
	const copies = countCopies(card.id);
	shell.innerHTML = `
		<div class="deck-dots" title="${copies}/${limit} in deck">${dotsHtml(limit, copies)}</div>
		<img class="gallery-card-img" src="${src}" alt="${escapeHtml(card.name)}" draggable="false">
		${arts.length >= 1 ? `
			<div class="gallery-art-switch">
				<span class="js-art-prev" title="Previous art">⬅️</span>
				<button class="js-art-pick art-count-btn" title="Browse all artworks">${artIndex + 1}/${arts.length}</button>
				<span class="js-art-next" title="Next art">➡️</span>
			</div>` : ''}`;
	const img = /** @type {HTMLElement|null} */ (shell.querySelector('.gallery-card-img'));
	if (img) wireCardPress(img, () => addCard(card), () => showImageZoom(src, card.name));
	shell.querySelector('.js-art-prev')?.addEventListener('click', () => void switchPoolArt(card, shell, false));
	shell.querySelector('.js-art-next')?.addEventListener('click', () => void switchPoolArt(card, shell, true));
	shell.querySelector('.js-art-pick')?.addEventListener('click', () => openArtPicker(card, () => {
		shell.innerHTML = cardLoadingHtml(card);
		void fillPoolShell(card, shell, buildStamp);
	}));
}

/**
 * TAP vs LONG-PRESS on a pool card: a short tap adds a copy; holding ~350ms
 * zooms the card fullscreen (and the release that follows does NOT also add).
 * @param {HTMLElement} el
 * @param {() => void} onTap
 * @param {() => void} onLongPress
 */
function wireCardPress(el, onTap, onLongPress) {
	let timer = 0;
	let longFired = false;
	// If the hold opened the fullscreen peek, lifting your finger (or the pointer
	// leaving/cancelling) ends it — so release closes the zoom, no stuck overlay.
	const endPeek = () => {
		window.clearTimeout(timer);
		if (longFired) hideImageZoom();
	};
	el.addEventListener('pointerdown', () => {
		longFired = false;
		timer = window.setTimeout(() => { longFired = true; onLongPress(); }, 350);
	});
	el.addEventListener('pointerup', () => {
		window.clearTimeout(timer);
		if (longFired) hideImageZoom();
		else onTap();
	});
	el.addEventListener('pointerleave', endPeek);
	el.addEventListener('pointercancel', endPeek);
	el.addEventListener('contextmenu', (e) => e.preventDefault()); // long-press menu
}

/**
 * Cycle a pool card to the next/previous collected art and re-fill its shell
 * (same as the cardpool's switcher). The re-fill re-reads copies, so the dots
 * stay correct.
 * @param {any} card
 * @param {HTMLElement} shell
 * @param {boolean} forward
 */
async function switchPoolArt(card, shell, forward) {
	const arts = card.collectedArts || [];
	if (!arts.length) return;
	let current = arts.indexOf(card.selectedArt);
	if (current === -1) current = 0;
	const next = forward ? (current + 1) % arts.length : (current - 1 + arts.length) % arts.length;
	card.selectedArt = arts[next];
	rememberArt(card.id, card.selectedArt); // an explicit pick sticks across sessions
	shell.innerHTML = cardLoadingHtml(card);
	await fillPoolShell(card, shell, buildStamp);
}

/**
 * Add a copy (respecting the per-card limit — old AddCard :3248).
 * @param {any} card
 */
function addCard(card) {
	if (readOnly) return;
	const decksize = deckSizeFor();
	if (deck.list.length >= decksize) {
		toast(`Deck is full (${decksize} cards).`);
		return;
	}
	if (countCopies(card.id) >= card.limit) {
		toast(`Only ${card.limit} cop${card.limit === 1 ? 'y' : 'ies'} of ${card.name} allowed.`);
		return;
	}
	// Remember the art the player is looking at in the pool for this copy.
	deck.list.push({ id: card.id, art: 0, selectedArt: card.selectedArt || 0 });
	deck.list.sort((a, b) => cardSortValue(store.cards[a.id]) - cardSortValue(store.cards[b.id]));
	deck.dateModified = Date.now();
	// INCREMENTAL: light one more dot, refresh the deck list + toolbar. The pool
	// grid and its images are never rebuilt, so there's no flicker.
	updateCardDots(card.id);
	paintCurrentDeck();
	updateHeaderState();
}

/** Copy a quantity-grouped decklist (old CopyToClipboard :2974). */
function copyDecklist() {
	const { cards } = store;
	/** @type {Array<{name: string, count: number}>} */
	const grouped = [];
	for (const entry of deck.list) {
		const name = cards[entry.id]?.name || 'Unknown';
		if (grouped.length && grouped.at(-1)?.name === name) {
			grouped.at(-1).count++;
		} else {
			grouped.push({ name, count: 1 });
		}
	}
	let text = (deck.nickname || 'Deckbiter') + ' Decklist:\n';
	// The leader is part of the deck — a shared list is ambiguous without it.
	const leaderName = store.heros?.[deck.hero]?.name
		|| HERO_ASSET_NAMES[deck.hero]?.toUpperCase();
	if (leaderName) {
		text += `Leader: ${leaderName}\n`;
	}
	for (const { name, count } of grouped) {
		text += `● ${name} x${count}\n`;
	}
	navigator.clipboard.writeText(text).then(
		() => toast('Decklist copied! 📋'),
		() => toast('Could not copy.', { error: true }),
	);
}

/**
 * Save — or delete, when trimmed to ≤15 cards (old SaveDeck :3282; the
 * "EATING YOUR DECK" rule kept, with an explicit confirm).
 */
async function saveDeck() {
	if (!can('saveDeck')) {
		showUpsell('saveDeck');
		return;
	}
	const nameInput = /** @type {HTMLInputElement|null} */ (screenEl?.querySelector('#deckname'));
	deck.nickname = nameInput?.value || deck.nickname || '';
	deck.dateModified = Date.now();
	// ≤15 cards deletes the deck outright — no confirm (owner's call; the
	// "EATING YOUR DECK" modal was more friction than protection).
	const isDelete = deck.list.length <= 15;

	callStatsAPI('Deckbuilding:Save');
	callStatsAPI('Deckbuilding:' + (store.heros[deck.hero]?.name || ''));
	// request() THROWS on a non-2xx (a handler crash 500s) and on network
	// failure — without this catch the whole save/delete died silently: no
	// toast, no state change, "deleting decks doesn't seem to function".
	let returned;
	try {
		returned = await request('Save Deck', deck);
	} catch {
		toast('Could not reach the server — nothing was changed.', { error: true });
		return;
	}
	// Any error-shaped response is a FAILURE — the old freeUser refusal came
	// back as {error:'freeUser'} with no `success` field, slipped past this
	// check, and the client toasted "saved" for a deck the server dropped.
	if (!returned || returned.success === false || returned.error) {
		const reason = returned?.error === 'invalid_deck'
			? 'the server says this deck is not legal'
			: returned?.error || 'server error';
		toast('Deck not saved — ' + reason + '.', { error: true });
		return;
	}

	if (isDelete) {
		// Compact the deck array exactly like the old client (:3316).
		const userData = store.userData;
		userData.decks.splice(deck.index, 1);
		userData.decks.forEach((d, index) => {
			if (d) d.index = index;
		});
		while (userData.decks.length < 100) {
			userData.decks.push(false);
		}
		notify('userData');
		toast('Deck devoured. 🍽️');
		navigate('lobby');
		return;
	}

	notify('userData');
	selectDeck(deck, deck.index); // saved deck becomes the loaded one
	toast('Deck saved ✔️');
	void paint();
}
